import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, UrlTree } from '@angular/router';

/**
 * A legacy-path redirect that keeps the query string and the fragment.
 *
 * Angular 17's string `redirectTo` only carries `:name` route params across: every query param is dropped.
 * Old sign-up links (/auth/signup, /join, …) still sit in marketing posts, emails and other sites, so
 * `/auth/signup?utm_source=…&gclid=…` used to land on `/sign-up` with no campaign data left for
 * TrackingResolver to store. This route redirects from a guard instead, which sees the whole URL.
 *
 * `to` is an absolute path whose `:name` segments are filled from the matched route's params, as with
 * `redirectTo`. `children: []` is what makes a component-less route with a guard a valid route config.
 * Once the app is on Angular 18+, a `redirectTo` function can replace this.
 */
export function keepQueryRedirect(path: string, to: string): Route {
    return {
        path,
        pathMatch: 'full',
        canActivate: [(route: ActivatedRouteSnapshot): UrlTree => {
            // segment commands (not parseUrl), so a param such as an org slug isn't decoded twice
            const segments = to.split('/').filter(Boolean).map(segment => segment.startsWith(':') ? route.params[segment.slice(1)] : segment);
            return inject(Router).createUrlTree(['/', ...segments], {
                queryParams: route.queryParams,
                fragment: route.fragment ?? undefined,
            });
        }],
        children: [],
    };
}
